// Inline-SVG cash-flow forecast chart. Six-month rolling window of stacked
// bars (paid + projected + invoice-due) per month. No external chart lib —
// keeps the producer SPA bundle small and the rendering deterministic for
// snapshot tests.

import { Card } from '@ohear/ui'
import { useMemo } from 'react'
import type { MonthBucket } from '../../lib/api/milestones'

export interface CashFlowChartProps {
  buckets: MonthBucket[]
  /** "Today's" month in YYYY-MM. Used to highlight the current bar. */
  currentMonth: string
}

export function CashFlowChart({ buckets, currentMonth }: CashFlowChartProps) {
  const window = useMemo(() => buckets.slice(-6), [buckets])
  const max = useMemo(() => {
    let m = 0
    for (const b of window) {
      const total = b.invoice_due_usd + b.paid_actor_usd + b.projected_actor_usd
      if (total > m) m = total
    }
    return Math.max(m, 1)
  }, [window])

  if (window.length === 0) {
    return (
      <Card title="Cash flow forecast">
        <p className="m-0 text-[13px] text-[var(--text-2)]">
          No milestones scheduled yet. Approve a batch to populate the forecast.
        </p>
      </Card>
    )
  }

  const W = 480
  const H = 180
  const PAD = { top: 10, right: 8, bottom: 24, left: 8 }
  const innerW = W - PAD.left - PAD.right
  const innerH = H - PAD.top - PAD.bottom
  const barWidth = innerW / window.length - 8

  return (
    <Card title="Cash flow forecast (last 6 months)">
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Cash flow forecast chart">
        {window.map((b, i) => {
          const x = PAD.left + i * (innerW / window.length) + 4
          const paidH = (b.paid_actor_usd / max) * innerH
          const projH = (b.projected_actor_usd / max) * innerH
          const invH = (b.invoice_due_usd / max) * innerH
          const isCurrent = b.month === currentMonth
          const yPaid = PAD.top + innerH - paidH
          const yProj = yPaid - projH
          const yInv = yProj - invH
          return (
            <g key={b.month}>
              {/* Invoice due (revenue, top of bar) */}
              <rect x={x} y={yInv} width={barWidth} height={invH} fill="var(--green)" rx={2} />
              {/* Projected actor payouts */}
              <rect x={x} y={yProj} width={barWidth} height={projH} fill="var(--amber)" rx={2} />
              {/* Paid actor payouts */}
              <rect x={x} y={yPaid} width={barWidth} height={paidH} fill="var(--accent)" rx={2} />
              <text
                x={x + barWidth / 2}
                y={H - 8}
                textAnchor="middle"
                fontSize={10}
                fill={isCurrent ? 'var(--text)' : 'var(--text-2)'}
                fontWeight={isCurrent ? 600 : 400}
              >
                {b.month.slice(5)}
              </text>
            </g>
          )
        })}
      </svg>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-[var(--text-2)]">
        <Legend swatch="var(--green)" label="Invoice due (revenue)" />
        <Legend swatch="var(--amber)" label="Projected actor payouts" />
        <Legend swatch="var(--accent)" label="Paid actor payouts" />
      </div>
    </Card>
  )
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="inline-block h-2 w-2 rounded-[var(--radius-sm)]"
        style={{ background: swatch }}
        aria-hidden="true"
      />
      {label}
    </span>
  )
}
